<script>
import dateFormat from "@/shared/lib/date-format";
import InformationLayout from "@/shared/ui/information-layout/InformationLayout.vue";
import SelectReference from "@/pages/admin/tariffs/components/SelectReference.vue";
import {
  LocalityType,
  LocalityTypeLabel,
  ParticipantType,
  ParticipantTypeLabel,
  TypeCalculation,
  TypeCalculationLabel,
} from "@/shared/config/tariffs";

import Radio from "@/shared/ui/radio/Radio.vue";
import RadioGroup from "@/shared/ui/radio/RadioGroup.vue";

import { FormItem, FormLabel, FormControl } from "@/shared/ui/form";
import { Input } from "@/shared/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/ui/select";
import { ChevronDown, X, Check } from "lucide-vue-next";
import Checkbox from "@/shared/ui/checkbox/Checkbox.vue";

export default {
  name: "TariffData",
  components: {
    InformationLayout,
    SelectReference,
    Radio,
    RadioGroup,
    FormItem,
    FormLabel,
    FormControl,
    Input,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
    ChevronDown,
    X,
    Check,
    Checkbox,
  },
  props: {
    form: {
      type: Object,
      default: () => ({}),
    },
    selectedTariff: {
      type: Object,
      default: () => null,
    },
  },
  data() {
    return {
      loading: false,
      showSelectDrawer: false,
      hiddenFieldsOpen: false,
      errors: {
        name: "",
        name_kz: "",
        codes: "",
        participant_type: "",
        locality: "",
        value: "",
        type_calculation: "",
      },
    };
  },
  watch: {
    "form.name"(val) {
      if (val) this.errors.name = "";
    },
    "form.name_kz"(val) {
      if (val) this.errors.name_kz = "";
    },
    "form.codes"(val) {
      if (val?.length) this.errors.codes = "";
    },
    "form.participant_type"(val) {
      if (val) this.errors.participant_type = "";
    },
    "form.locality"(val) {
      if (val) this.errors.locality = "";
    },
    "form.value"(val) {
      if (val) this.errors.value = "";
    },
    "form.type_calculation"(val) {
      if (val) this.errors.type_calculation = "";
    },
  },
  mounted() {
    document.addEventListener("pointerdown", this.onHiddenFieldsClickOutside);
  },
  beforeUnmount() {
    document.removeEventListener(
      "pointerdown",
      this.onHiddenFieldsClickOutside
    );
  },
  computed: {
    hiddenFieldsLabel() {
      if (!this.form.hidden_fields?.length) return "";
      const names = this.form.hidden_fields.map((key) => {
        const field = this.hidden_field_list?.find((f) => f.key === key);
        return field ? field.name : key;
      });
      return names.join(", ");
    },
    TypeCalculationLabel() {
      return TypeCalculationLabel;
    },
    TypeCalculation() {
      return TypeCalculation;
    },
    ParticipantTypeLabel() {
      return ParticipantTypeLabel;
    },
    ParticipantType() {
      return ParticipantType;
    },
    LocalityTypeLabel() {
      return LocalityTypeLabel;
    },
    LocalityType() {
      return LocalityType;
    },
    uuid() {
      return this.$route.params.id;
    },
    showForm() {
      return (
        this.$route.name === "tariff_new" || this.$route.name === "tariff_item"
      );
    },
    typeTariff() {
      return [
        {
          value: "FIXED",
          name: this.$t("Фиксированный"),
          name_kz: "Бекітілген",
          name_en: "Fixed",
        },
        {
          value: "PER_PAGE",
          name: this.$t("За страницу"),
          name_kz: "Бет үшін",
          name_en: "Per page",
        },
        {
          value: "PER_DOCUMENT",
          name: this.$t("За документ"),
          name_kz: "Құжат үшін",
          name_en: "Per document",
        },
        {
          value: "PER_INHERITANCE_CERTIFICATE",
          name: this.$t("За выдачу свидетельства о наследстве"),
          name_kz: "Мұраға құқық туралы куәлікті беру үшін",
          name_en: "Per inheritance certificate issuance",
        },
        {
          value: "PER_MONTH",
          name: this.$t("За каждый месяц"),
          name_kz: "Әр ай үшін",
          name_en: "Per month",
        },
      ];
    },
    selectedExtraName: {
      get() {
        if (!this.form?.extra_names) {
          return "FIXED";
        }

        const empty =
          typeof this.form.extra_names === "object" &&
          Object.keys(this.form.extra_names).length === 0;

        if (empty) {
          return "FIXED";
        }

        return this.form.extra_names.value || "FIXED";
      },

      set(val) {
        if (val === "FIXED") {
          this.form.extra_names = null;
        } else {
          const found = this.typeTariff.find((t) => t.value === val);
          this.form.extra_names = found || null;
        }
      },
    },

    relativesString: {
      get() {
        if (this.form.relatives === true) return "true";
        if (this.form.relatives === false) return "false";
        return undefined;
      },
      set(val) {
        this.form.relatives = val === "true";
      },
    },

    sections() {
      if (!this.selectedTariff) return [];

      const tariff = this.selectedTariff;

      return [
        {
          title: this.$t("Данные тарифа"),
          items: [
            {
              label: this.$t("Наименование тарифа"),
              values: [
                { lang: "Рус", text: tariff.name },
                { lang: "Қаз", text: tariff.name_kz },
              ],
            },
            {
              label: this.$t("Дата создания"),
              values: [{ text: this.dateFormat(tariff.created_at) }],
            },
            {
              label: this.$t("Категория нотариального действия"),
              values: [{ text: tariff.codes?.length }],
            },
            {
              label: this.$t("Тип тарифа"),
              values: [
                {
                  text:
                    this.typeTariff.find(
                      (t) => t.value === tariff.extra_names?.value
                    )?.name || this.$t("Фиксированный"),
                },
              ],
            },
            {
              label: this.$t("Тип лица"),
              values: [
                {
                  text:
                    this.ParticipantTypeLabel[tariff.participant_type] || "-",
                },
              ],
            },
            {
              label: this.$t("Тип местности"),
              values: [
                { text: this.LocalityTypeLabel[tariff.locality] || "-" },
              ],
            },
            {
              label: this.$t("Ограничение не менее (МРП)"),
              values: [{ text: tariff.amount_from || "-" }],
            },
            {
              label: this.$t("Ограничение не более (МРП)"),
              values: [{ text: tariff.amount_to || "-" }],
            },
            {
              label: this.$t("Ставка тарифа"),
              values: [
                {
                  text:
                    `${tariff.value} ${
                      this.TypeCalculationLabel[tariff.type_calculation]
                    }` || "-",
                },
              ],
            },
            {
              label: this.$t("Тип расчета"),
              values: [
                {
                  text:
                    this.TypeCalculationLabel[tariff.type_calculation] || "-",
                },
              ],
            },
            {
              label: this.$t("Родственник"),
              values: [
                { text: tariff.relatives ? this.$t("Да") : this.$t("Нет") },
              ],
            },
          ],
        },
      ];
    },

    displayValue() {
      return this.form?.codes.length
        ? this.$t("Выбрано: {count}", { count: this.form?.codes.length })
        : "";
    },
    hidden_field_list() {
      return [
        {
          key: "locality",
          name: this.$t("Тип местности"),
        },
        {
          key: "relatives",
          name: this.$t("Родственник"),
        },
        {
          key: "amount",
          name: this.$t("Ограничение (в МРП)"),
        },
        {
          key: "participant_type",
          name: this.$t("Тип лица"),
        },
      ];
    },
  },
  methods: {
    async validate() {
      const required = this.$t("Обязательное поле");

      this.errors = {
        name: "",
        name_kz: "",
        codes: "",
        participant_type: "",
        locality: "",
        value: "",
        type_calculation: "",
      };

      let valid = true;

      if (!this.form.name) {
        this.errors.name = required;
        valid = false;
      }
      if (!this.form.name_kz) {
        this.errors.name_kz = required;
        valid = false;
      }
      if (!this.form.codes?.length) {
        this.errors.codes = required;
        valid = false;
      }
      if (!this.form.participant_type) {
        this.errors.participant_type = required;
        valid = false;
      }
      if (!this.form.locality) {
        this.errors.locality = required;
        valid = false;
      }
      if (!this.form.value) {
        this.errors.value = required;
        valid = false;
      }
      if (!this.form.type_calculation) {
        this.errors.type_calculation = required;
        valid = false;
      }

      return valid;
    },
    onHiddenFieldsClickOutside(event) {
      if (!this.hiddenFieldsOpen) return;
      if (!event.target.closest(".hidden-fields-select")) {
        this.hiddenFieldsOpen = false;
      }
    },
    toggleHiddenField(key) {
      const current = this.form.hidden_fields || [];
      if (current.includes(key)) {
        this.form.hidden_fields = current.filter((k) => k !== key);
      } else {
        this.form.hidden_fields = [...current, key];
      }
    },
    dateFormat(date) {
      if (!date) {
        return this.$t("Не указано");
      }
      return dateFormat.timestamp_to_local_tz(date, "DD.MM.YYYY, HH:mm");
    },
  },
};
</script>

<template>
  <div class="block">
    <div v-if="showForm" class="flex flex-col gap-8 w-full">
      <div class="flex flex-col gap-3">
        <div class="text-foreground text-lg font-semibold leading-6">
          {{ $t("Параметры тарифа") }}
        </div>
      </div>

      <div class="flex flex-col gap-1">
        <div class="grid grid-cols-2 gap-3">
          <FormItem float>
            <FormLabel
              class="after:content-['*'] after:text-destructive after:ml-0.5"
            >
              {{ $t("Наименование тарифа (рус)") }}
            </FormLabel>
            <FormControl>
              <Input
                v-model="form.name"
                placeholder=""
                :aria-invalid="!!errors.name || undefined"
              />
            </FormControl>
          </FormItem>

          <FormItem float>
            <FormLabel
              class="after:content-['*'] after:text-destructive after:ml-0.5"
            >
              {{ $t("Наименование тарифа (қаз)") }}
            </FormLabel>
            <FormControl>
              <Input
                v-model="form.name_kz"
                placeholder=""
                :aria-invalid="!!errors.name_kz || undefined"
              />
            </FormControl>
          </FormItem>
        </div>

        <div class="grid grid-cols-1 gap-3">
          <FormItem float>
            <FormLabel
              class="after:content-['*'] after:text-destructive after:ml-0.5"
            >
              {{ $t("Категория нотариального действия") }}
            </FormLabel>
            <FormControl>
              <div class="relative">
                <Input
                  :model-value="displayValue"
                  readonly
                  :placeholder="$t('Выберите')"
                  class="cursor-pointer pr-8"
                  :aria-invalid="!!errors.codes || undefined"
                  @click="showSelectDrawer = true"
                />
                <ChevronDown
                  class="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground cursor-pointer"
                  @click="showSelectDrawer = true"
                />
              </div>
            </FormControl>
          </FormItem>
        </div>

        <div class="flex flex-col pb-6">
          <div class="text-xs text-muted-foreground">
            {{ $t("Тип тарифа") }}
          </div>
          <RadioGroup v-model="selectedExtraName">
            <Radio
              v-for="item in typeTariff"
              :key="item.value"
              :value="item.value"
            >
              {{ item.name }}
            </Radio>
          </RadioGroup>
        </div>

        <div class="flex gap-3">
          <FormItem float class="flex-1">
            <FormLabel
              class="after:content-['*'] after:text-destructive after:ml-0.5"
            >
              {{ $t("Тип лица") }}
            </FormLabel>
            <FormControl>
              <Select v-model="form.participant_type">
                <SelectTrigger
                  class="w-full"
                  :aria-invalid="!!errors.participant_type || undefined"
                  @clear="form.participant_type = null"
                >
                  <SelectValue :placeholder="$t('Выберите')" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem
                    v-for="item in ParticipantType"
                    :key="item"
                    :value="String(item)"
                  >
                    {{ ParticipantTypeLabel[item] }}
                  </SelectItem>
                </SelectContent>
              </Select>
            </FormControl>
          </FormItem>

          <FormItem float class="flex-1">
            <FormLabel
              class="after:content-['*'] after:text-destructive after:ml-0.5"
            >
              {{ $t("Тип местности") }}
            </FormLabel>
            <FormControl>
              <Select v-model="form.locality">
                <SelectTrigger
                  class="w-full"
                  :aria-invalid="!!errors.locality || undefined"
                  @clear="form.locality = null"
                >
                  <SelectValue :placeholder="$t('Выберите')" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem
                    v-for="item in LocalityType"
                    :key="item"
                    :value="String(item)"
                  >
                    {{ LocalityTypeLabel[item] }}
                  </SelectItem>
                </SelectContent>
              </Select>
            </FormControl>
          </FormItem>
        </div>

        <div class="flex gap-3">
          <FormItem float class="flex-1">
            <FormLabel>{{ $t("Ограничение не менее (МРП)") }}</FormLabel>
            <FormControl>
              <Input v-model="form.amount_from" placeholder="" class="w-full" />
            </FormControl>
          </FormItem>

          <FormItem float class="flex-1">
            <FormLabel>{{ $t("Ограничение не более (МРП)") }}</FormLabel>
            <FormControl>
              <Input v-model="form.amount_to" placeholder="" class="w-full" />
            </FormControl>
          </FormItem>
        </div>

        <div class="flex gap-3">
          <FormItem float class="flex-1">
            <FormLabel
              class="after:content-['*'] after:text-destructive after:ml-0.5"
            >
              {{ $t("Ставка тарифа") }}
            </FormLabel>
            <FormControl>
              <Input
                v-model="form.value"
                placeholder=""
                class="w-full"
                :aria-invalid="!!errors.value || undefined"
              />
            </FormControl>
          </FormItem>

          <FormItem float class="flex-1">
            <FormLabel
              class="after:content-['*'] after:text-destructive after:ml-0.5"
            >
              {{ $t("Тип расчета") }}
            </FormLabel>
            <FormControl>
              <Select v-model="form.type_calculation">
                <SelectTrigger
                  class="w-full"
                  :aria-invalid="!!errors.type_calculation || undefined"
                  @clear="form.type_calculation = null"
                >
                  <SelectValue :placeholder="$t('Выберите')" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem
                    v-for="item in TypeCalculation"
                    :key="item"
                    :value="String(item)"
                  >
                    {{ TypeCalculationLabel[item] }}
                  </SelectItem>
                </SelectContent>
              </Select>
            </FormControl>
          </FormItem>
        </div>

        <FormItem float>
          <FormLabel>{{ $t("Родственник") }}</FormLabel>
          <FormControl>
            <Select v-model="relativesString">
              <SelectTrigger class="w-full" @clear="form.relatives = null">
                <SelectValue :placeholder="$t('Выберите')" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="true">{{ $t("Да") }}</SelectItem>
                <SelectItem value="false">{{ $t("Нет") }}</SelectItem>
              </SelectContent>
            </Select>
          </FormControl>
        </FormItem>

        <FormItem>
          <FormLabel>{{
            $t("Скрываемые поля внутри нотариального действия")
          }}</FormLabel>
          <FormControl>
            <div class="relative hidden-fields-select">
              <div
                class="multi-select-trigger"
                @click="hiddenFieldsOpen = !hiddenFieldsOpen"
              >
                <span
                  v-if="!form.hidden_fields?.length"
                  class="text-muted-foreground text-sm"
                >
                  {{ $t("Выберите поля для скрытия") }}
                </span>
                <span v-else class="text-foreground text-sm truncate">
                  {{ hiddenFieldsLabel }}
                </span>
                <span class="flex items-center gap-1 ml-auto shrink-0">
                  <button
                    v-if="form.hidden_fields?.length"
                    type="button"
                    class="text-muted-foreground hover:text-foreground"
                    @click.stop="form.hidden_fields = []"
                  >
                    <X class="w-3.5 h-3.5" />
                  </button>
                  <ChevronDown class="w-4 h-4 opacity-50" />
                </span>
              </div>
              <div
                v-if="hiddenFieldsOpen"
                class="absolute z-50 mt-1 w-full rounded-md border bg-popover p-1 shadow-md max-h-60 overflow-auto"
              >
                <div
                  v-for="field in hidden_field_list"
                  :key="field.key"
                  class="flex items-center gap-2 rounded-sm px-2 py-1.5 text-sm cursor-pointer hover:bg-accent"
                  @click="toggleHiddenField(field.key)"
                >
                  <Checkbox
                    :model-value="form.hidden_fields?.includes(field.key)"
                    @click.stop
                    @change="toggleHiddenField(field.key)"
                  />
                  <span class="text-sm text-foreground">{{ field.name }}</span>
                </div>
              </div>
            </div>
          </FormControl>
        </FormItem>
      </div>
    </div>
    <div v-if="$route.name === 'tariff_item_view'">
      <InformationLayout :showHeader="false" :sections="sections" />
    </div>

    <SelectReference
      v-if="showSelectDrawer"
      :form="form"
      @close="showSelectDrawer = false"
      @save="showSelectDrawer = false"
    />
  </div>
</template>

<style scoped lang="scss">
.block {
  @apply h-full w-1/2 overflow-auto bg-card rounded-2xl p-8;
}

.multi-select-trigger {
  @apply flex min-h-9 w-full items-center rounded-md border border-input bg-transparent px-3 py-1 text-sm cursor-pointer;
  @apply focus-visible:border-primary focus-visible:ring-ring/50 focus-visible:ring-[3px];
  @apply transition-[color,box-shadow] outline-none;
}

@media (max-width: 1400px) {
  .block {
    @apply w-full;
  }
}
</style>
